import { useContext, useEffect, useState } from 'react';
import { RxHamburgerMenu } from 'react-icons/rx';
import { FaRegUserCircle, FaUserEdit } from 'react-icons/fa';
import { Container } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FiLogOut } from 'react-icons/fi';
import { IoAccessibility, IoInformationCircleOutline } from 'react-icons/io5';
import { PiMapPinLineFill } from 'react-icons/pi';
import Cookies from 'js-cookie';
import { clearCookies } from 'utils/cookieUtils';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { usePerms } from 'contexts/PermsContext';
import useTextToSpeech from 'hooks/useTextToSpeech';
import styles from './assets/header.module.css';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { logout } from 'services/auth/auth.service';
import { getUserPlacesData, updateUserPlacesData } from 'services/user/places.service';
import { getLogo } from 'utils/imageUtils';

const MobileHeader = ({
  isMobileNavOpen,
  setShowFull,
  setNewTermsFullyAgreed,
  setTermsFullyViewed,
  setHasSignedIn,
  setHasFetchedPerms,
  setCookiesAgreed,
  setPrivacyAgreed,
}) => {
  const [location, setLocation] = useState(null);
  const [userMenuOverlay, setUserMenuOverlay] = useState(false);
  const [places, setPlaces] = useState([]);
  const tts = useTextToSpeech();
  const perms = usePerms();
  const isMsalAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { logout: authLogout } = authContext;

  const handleLogout = () => {
    if (isMsalAuthenticated) {
      instance.logoutRedirect({
        onRedirectNavigate: (url) => {
          return false;
        },
      });
    }
    setShowFull(true);

    // Call API logout
    logout();

    // Keep remember me data if set
    let keepCookies = [];
    if (Cookies.get('rememberMe') === 'true') {
      keepCookies = ['rememberMe', 'email'];
    }
    clearCookies(keepCookies);

    // Explicitly clear privacy and cookie agreement for legal compliance
    Cookies.remove('cookiesAgreed');
    Cookies.remove('privacyAgreed');
    setCookiesAgreed(false);
    setPrivacyAgreed(false);

    // Reset app state
    perms.reset();
    tts.cancel();
    setNewTermsFullyAgreed(false);
    setTermsFullyViewed(false);
    setHasSignedIn(false);
    setHasFetchedPerms(false);

    // Use centralized auth logout
    authLogout();

    navigate('/');
  };

  const selectLocation = (location) => {
    if (!location) return;
    setLocation(location);
  };

  const addDefaultLocationIfEmpty = () => {
    let defaultPlace = {
      city: 'Dallas',
      DefaultLocationUserHome: false,
      term: 'Dallas',
      type: 'locality',
      title: 'Dallas',
      geometry: {
        location: {
          lat: 32.7766642,
          lng: -96.7969879,
        },
        viewport: {
          northeast: {
            lat: 33.0237920649461,
            lng: -96.46375805411309,
          },
          southwest: {
            lat: 32.6175369971454,
            lng: -96.9993469629029,
          },
        },
      },
      latitude: 32.7766642,
      place_id: 'ChIJS5dFe_cZTIYRj2dH9qSb7Lk',
      longitude: -96.7969879,
      place_data: {
        name: 'Dallas',
        types: ['locality', 'political'],
        geometry: {
          location: {
            lat: 32.7766642,
            lng: -96.7969879,
          },
          viewport: {
            northeast: {
              lat: 33.0237920649461,
              lng: -96.46375805411309,
            },
            southwest: {
              lat: 32.6175369971454,
              lng: -96.9993469629029,
            },
          },
        },
        formatted_address: 'Dallas, TX, USA',
        address_components: [
          {
            types: ['locality', 'political'],
            long_name: 'Dallas',
            short_name: 'Dallas',
          },
          {
            types: ['administrative_area_level_2', 'political'],
            long_name: 'Dallas County',
            short_name: 'Dallas County',
          },
          {
            types: ['administrative_area_level_1', 'political'],
            long_name: 'Texas',
            short_name: 'TX',
          },
          {
            types: ['country', 'political'],
            long_name: 'United States',
            short_name: 'US',
          },
        ],
      },
      state_long: 'Texas',
      state_short: 'TX',
    };
    if (places.length === 0) {
      setPlaces([defaultPlace]);
      updateUserPlacesData([defaultPlace]);
      setLocation(defaultPlace);
    }
  };

  useEffect(() => {
    (async () => {
      const response = await getUserPlacesData();

      const placeList = response.data.places || [];
      setPlaces(placeList);

      if (Cookies.get('selectedLocationIndex') > placeList.length - 1) {
        Cookies.set('selectedLocationIndex', 0);
      }

      if (!Cookies.get('selectedLocationIndex')) {
        Cookies.set('selectedLocationIndex', 0);
      }

      if (placeList.length === 0) {
        addDefaultLocationIfEmpty();
      } else {
        selectLocation(placeList[Cookies.get('selectedLocationIndex')]);
      }
    })();
  }, []);

  return (
    <>
      <Container
        as="header"
        fluid
        id="mobile-header"
        className={`d-flex justify-content-around p-3 w-100 align-items-center bg-primary ${
          userMenuOverlay && 'position-fixed z-3'
        } `}
      >
        <RxHamburgerMenu
          size={30}
          onClick={() => {
            isMobileNavOpen(true);
            setUserMenuOverlay(false);
          }}
        />
        <img src={getLogo(true)} alt="logo" height={59} />
        <FaRegUserCircle size={30} onClick={() => setUserMenuOverlay(!userMenuOverlay)} />
      </Container>
      <Container
        fluid
        className={`bg-primary-shadow z-3 py-0 px-0 d-flex flex-column gap-4 position-fixed ${
          styles['user-menu']
        } ${userMenuOverlay && styles['user-menu-open']}`}
      >
        <div className="w-100 p-3 d-flex flex-column gap-3" style={{ background: '#232B33' }}>
          <div>
            <p className="fs-5 mb-0">Signed in as:</p>
            <p className="fs-5 mb-0">{Cookies.get('username')}</p>
          </div>
          <div>
            <p className="fs-5 mb-0">Currently in:</p>
            {location?.city && <p className="fs-5 mb-0">{location.city}</p>}
          </div>
        </div>
        <div className="px-3">
          <Link
            to="/usermenu?tab=Account"
            className="text-white text-decoration-none d-flex gap-3 align-items-center fs-5"
            onClick={() => setUserMenuOverlay(false)}
          >
            <FaUserEdit size={45} />
            Edit Account
          </Link>
        </div>
        <div className="px-3">
          <Link
            to="/usermenu?tab=Places"
            className="text-white text-decoration-none d-flex gap-3 align-items-center fs-5"
            onClick={() => setUserMenuOverlay(false)}
          >
            <PiMapPinLineFill size={45} />
            Places
          </Link>
        </div>
        <div className="px-3">
          <Link
            to="/usermenu?tab=Accessibility"
            className="text-white text-decoration-none d-flex gap-3 align-items-center fs-5"
            onClick={() => setUserMenuOverlay(false)}
          >
            <IoAccessibility size={45} />
            Accessibility Settings
          </Link>
        </div>
        <div className="px-3">
          <Link
            to="usermenu?tab=About"
            className="text-white text-decoration-none d-flex gap-3 align-items-center fs-5"
            onClick={() => setUserMenuOverlay(false)}
          >
            <IoInformationCircleOutline size={45} />
            About
          </Link>
        </div>
        <div className="px-3">
          <Link
            to="/"
            className="text-white text-decoration-none d-flex gap-3 align-items-center fs-5 mb-5"
            onClick={() => {
              setUserMenuOverlay(false);
              handleLogout();
            }}
          >
            <FiLogOut size={45} />
            Log Out
          </Link>
        </div>
      </Container>
    </>
  );
};

export default MobileHeader;
